(function () {
  // Opens form modals by matching clicked trigger keys to app form IDs.
  const SECTION_SELECTOR = '[data-flexible-form-modal-section]';
  const TRIGGER_SELECTOR = '[data-form-trigger]';

  function waitForHost(selector, timeout = 10000, scope = document) {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const tick = () => {
        const host = scope?.querySelector?.(selector);
        if (host?.shadowRoot) return resolve(host);

        if (Date.now() - start > timeout) {
          return reject(new Error(`Host not ready: ${selector}`));
        }
        requestAnimationFrame(tick);
      };

      tick();
    });
  }

  function normalizeFormKey(value) {
    const key = String(value || '').trim();
    if (!key) {
      return '';
    }

    if (key.startsWith('forms-root-')) {
      return key;
    }

    // Triggers can be numeric IDs; app blocks typically use forms-root-*.
    if (/^\d+$/.test(key)) {
      return `forms-root-${key}`;
    }

    return key;
  }

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex^="-"])',
  ].join(',');

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  // Collect focusable elements in DOM order, descending into open shadow roots
  // so Shopify Forms fields (rendered inside <shopify-forms-embed>) are included.
  function collectFocusable(root, acc) {
    root.querySelectorAll('*').forEach((el) => {
      if (el.matches(FOCUSABLE_SELECTOR) && el.tabIndex !== -1 && isVisible(el)) {
        acc.push(el);
      }
      if (el.shadowRoot) {
        collectFocusable(el.shadowRoot, acc);
      }
    });
    return acc;
  }

  // Resolve the truly-focused element across shadow boundaries.
  function deepActiveElement() {
    let el = document.activeElement;
    while (el && el.shadowRoot && el.shadowRoot.activeElement) {
      el = el.shadowRoot.activeElement;
    }
    return el;
  }

  function isNativeInteractive(el) {
    const tagName = el.tagName.toLowerCase();
    return (
      tagName === 'button' ||
      tagName === 'input' ||
      tagName === 'select' ||
      tagName === 'textarea' ||
      ((tagName === 'a' || tagName === 'area') && el.hasAttribute('href'))
    );
  }

  function focusElement(el) {
    if (!el || typeof el.focus !== 'function') {
      return;
    }

    try {
      el.focus({ preventScroll: true });
    } catch (error) {
      el.focus();
    }
  }

  class FlexibleFormModal {
    static instances = [];
    static hasGlobalListeners = false;

    constructor(section) {
      this.section = section;
      this.overlay = section.querySelector('[data-flexible-form-overlay]');
      this.modals = Array.from(section.querySelectorAll('[data-flexible-form-modal]'));
      this.modalByKey = this.buildModalKeyIndex();
      this.currentModal = null;
      this.lastTrigger = null;
      this.sectionId = section.id || `flexible-form-modal-${Math.random().toString(36).slice(2, 8)}`;

      this.mountToBody();
      this.bindOverlayEvents();
      FlexibleFormModal.instances.push(this);
      FlexibleFormModal.bindGlobalEvents();
      FlexibleFormModal.syncTriggerAttributes();
    }

    mountToBody() {
      // Keep overlay/modal at document root to avoid clipping/z-index issues.
      if (this.overlay) {
        this.overlay.setAttribute('data-flexible-form-owner', this.sectionId);
        document.body.appendChild(this.overlay);
      }

      this.modals.forEach((modal) => {
        modal.setAttribute('data-flexible-form-owner', this.sectionId);
        document.body.appendChild(modal);
      });
    }

    bindOverlayEvents() {
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeModal());
      }
    }

    static bindGlobalEvents() {
      if (FlexibleFormModal.hasGlobalListeners) {
        return;
      }

      FlexibleFormModal.hasGlobalListeners = true;
      document.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) {
          return;
        }

        const trigger = target.closest(TRIGGER_SELECTOR);

        // Close button inside any open modal.
        const closeButton = target.closest('[data-flexible-form-close]');
        if (closeButton) {
          const owner = FlexibleFormModal.instances.find(
            (instance) => instance.currentModal && instance.currentModal.contains(closeButton)
          );
          if (owner) {
            event.preventDefault();
            owner.closeModal();
            return;
          }
        }

        // Clicking outside any open modal should close it.
        let closedModal = false;
        FlexibleFormModal.instances.forEach((instance) => {
          if (instance.currentModal && !instance.currentModal.contains(target)) {
            instance.closeModal({ restoreFocus: !trigger });
            closedModal = true;
          }
        });

        if (closedModal) {
          event.preventDefault();
        }

        // Open the first matching modal for clicked trigger.
        if (!trigger) {
          return;
        }

        const triggerKey = normalizeFormKey(trigger.getAttribute('data-form-trigger'));
        if (!triggerKey) {
          return;
        }

        for (let i = 0; i < FlexibleFormModal.instances.length; i += 1) {
          const instance = FlexibleFormModal.instances[i];
          const modal = instance.getModalByTriggerKey(triggerKey);
          if (!modal) {
            continue;
          }

          event.preventDefault();
          FlexibleFormModal.closeAll({ restoreFocus: false });
          instance.openModal(modal, trigger);
          break;
        }
      });

      document.addEventListener('keydown', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest?.(TRIGGER_SELECTOR);

        if (trigger && !isNativeInteractive(trigger) && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          trigger.click();
          return;
        }

        if (event.key === 'Escape') {
          FlexibleFormModal.closeAll();
          return;
        }

        if (event.key !== 'Tab') {
          return;
        }

        const instance = FlexibleFormModal.instances.find((entry) => entry.currentModal);
        if (instance) {
          instance.handleTab(event);
        }
      });

      // Safety net: if focus escapes the open dialog (e.g. via mouse or a
      // programmatic move), pull it back so the modal behaves as a focus trap.
      document.addEventListener('focusin', (event) => {
        const instance = FlexibleFormModal.instances.find((entry) => entry.currentModal);
        if (!instance) {
          return;
        }

        const modal = instance.currentModal;
        const target = event.target;
        if (modal.contains(target)) {
          return;
        }

        const focusables = collectFocusable(modal, []);
        focusElement(focusables[0] || modal);
      });
    }

    static closeAll(options) {
      FlexibleFormModal.instances.forEach((instance) => instance.closeModal(options));
    }

    static syncTriggerAttributes(root = document) {
      root.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
        const triggerKey = normalizeFormKey(trigger.getAttribute('data-form-trigger'));
        if (!triggerKey) {
          return;
        }

        for (let i = 0; i < FlexibleFormModal.instances.length; i += 1) {
          const instance = FlexibleFormModal.instances[i];
          const modal = instance.getModalByTriggerKey(triggerKey);
          if (!modal) {
            continue;
          }

          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-expanded', instance.currentModal === modal ? 'true' : 'false');
          if (modal.id) {
            trigger.setAttribute('aria-controls', modal.id);
          }

          if (!isNativeInteractive(trigger)) {
            trigger.setAttribute('role', 'button');
            trigger.setAttribute('tabindex', '0');
            trigger.removeAttribute('aria-disabled');
          }
          break;
        }
      });
    }

    buildModalKeyIndex() {
      const map = new Map();

      this.modals.forEach((modal) => {
        this.getModalKeys(modal).forEach((key) => {
          if (!map.has(key)) {
            map.set(key, modal);
          }
        });
      });

      return map;
    }

    getModalByTriggerKey(triggerKey) {
      return this.modalByKey.get(triggerKey) || null;
    }

    getModalKeys(modal) {
      // App output can vary by context; collect all possible keys and match against any.
      const keys = [];

      const formId = modal.getAttribute('data-form-id');
      if (formId) {
        keys.push(normalizeFormKey(formId));
      }

      const formsId = modal.getAttribute('data-forms-id');
      if (formsId) {
        keys.push(normalizeFormKey(formsId));
      }

      const formKey = modal.getAttribute('data-form-key');
      if (formKey) {
        keys.push(normalizeFormKey(formKey));
      }

      // Shopify Forms app often renders the key on a nested data-form-root node.
      const nestedFormRoots = modal.querySelectorAll('[data-form-root][data-forms-id]');
      nestedFormRoots.forEach((node) => {
        keys.push(normalizeFormKey(node.getAttribute('data-forms-id')));
      });

      return Array.from(new Set(keys.filter(Boolean)));
    }

    openModal(modal, trigger) {
      this.closeModal({ restoreFocus: false });
      this.currentModal = modal;
      this.lastTrigger = trigger || null;

      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      if (this.overlay) {
        this.overlay.classList.add('open');
      }

      document.body.style.overflow = 'hidden';

      FlexibleFormModal.syncTriggerAttributes();
      this.applyActiveFormEnhancements(modal);
      this.focusModal(modal);
    }

    closeModal(options = {}) {
      const { restoreFocus = true } = options;
      const modal = this.currentModal;
      const trigger = this.lastTrigger;

      if (!modal) {
        return;
      }

      modal.classList.remove('open');

      if (this.overlay) {
        this.overlay.classList.remove('open');
      }

      this.currentModal = null;
      this.lastTrigger = null;
      document.body.style.overflow = 'auto';
      FlexibleFormModal.syncTriggerAttributes();

      // Restore the trigger's state and return focus so keyboard users
      // land back where they left off.
      if (restoreFocus && trigger && document.contains(trigger)) {
        focusElement(trigger);
      }

      modal.setAttribute('aria-hidden', 'true');
    }

    focusModal(modal) {
      // Move focus into the dialog on the next frame so it is announced and
      // keyboard users start inside the form rather than behind the overlay.
      requestAnimationFrame(() => {
        if (this.currentModal !== modal) {
          return;
        }
        focusElement(modal);
      });
    }

    handleTab(event) {
      const modal = this.currentModal;
      if (!modal) {
        return;
      }

      const focusables = collectFocusable(modal, []);
      if (focusables.length === 0) {
        // Nothing focusable yet (e.g. form still loading); keep focus on the dialog.
        event.preventDefault();
        focusElement(modal);
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = deepActiveElement();
      const index = focusables.indexOf(active);

      if (event.shiftKey) {
        if (active === first || index === -1) {
          event.preventDefault();
          focusElement(last);
        }
      } else if (active === last || index === -1) {
        event.preventDefault();
        focusElement(first);
      }
    }

    applyActiveFormEnhancements(modal) {
      if (!modal || modal.getAttribute('data-hide-app-title') !== 'true') {
        return;
      }

      this.findActiveFormHost(modal)
        .then((shadowHost) => {
          const root = shadowHost.shadowRoot;
          this.hideFormTitle(root);
          this.insertRalHelperText(root);
        })
        .catch(() => {
          // Embed not ready; skip title hiding without interrupting modal flow.
        });
    }

    findActiveFormHost(modal) {
      // Resolve the active modal's own Shopify form host so multiple forms can coexist.
      return waitForHost('shopify-forms-embed', 10000, modal);
    }

    hideFormTitle(root) {
      const title = root?.querySelector('._textHeading_2aowh_35');
      if (title) {
        title.style.display = 'none';
      }
    }

    findRalColorField(root) {
      return root
        ?.querySelector('[id="label-custom#ral_color"]')
        ?.closest('div[class*="_formFieldContainer_"]');
    }

    insertRalHelperText(root) {
      const ralColorField = this.findRalColorField(root);
      if (!ralColorField || ralColorField.querySelector('[data-ral-helper-text]')) {
        return;
      }

      const text = document.createElement('div');
      text.setAttribute('data-ral-helper-text', 'true');
      text.textContent = "Not sure about your RAL color code? Submit now and we'll follow up.";
      text.style.cssText = 'padding-top: 5px; color: rgba(32,32,32,0.5); font-size: 0.875rem; margin-bottom: 6px;';
      ralColorField.appendChild(text);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach((section) => new FlexibleFormModal(section));
  });
})();
